// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { nodeExistsInTree } from '../../../functions/node-exists-in-tree';
import { nodeListItemToCccAdapterTransitionTargetItem } from '../../../functions/transition-targets/node-list-item-to-ccc-adapter-transition-target-item';
import { targetHasActiveTransitionTargetData } from '../../../functions/transition-targets/target-has-active-transition-target-data';
import { ClientNode } from '../../../model/client-model/client-node';
import { ClientTarget } from '../../../model/client-model/client-target';
import { NodeType } from '../../../model/client-model/properties/node-type';
import { NodeListItem } from '../../../model/node-list-item';
import { CccAdapterTransitionTargetItem } from '../ccc-adapter/ccc-adapter-transition-target-item';
import { DEPLOYED_BUT_NOT_COMPATIBLE_WITH_ALGORITHM } from '../ccc-algorithm/ccc-algorithm.service';
import { KeyMap } from '../ccc-algorithm/key-map';
import { IClimateCortexModelService } from '../ccc-algorithm/model-service/ccc.model.abstract';

import { TransitionTargetSector } from '../../../feature/editor-targets-list/components/board-targets/sectors';
import { getSubsectorForTransitionElement } from '../../../functions/transition-targets/get-subsector-for-transition-element';
import { isAgriculture } from '../../../functions/type-guards/is-agriculture';
import { isEnergy } from '../../../functions/type-guards/is-energy';
import { isIndustry } from '../../../functions/type-guards/is-industry';
import { isOther } from '../../../functions/type-guards/is-other';
import { isTransport } from '../../../functions/type-guards/is-transport';
import { unique } from '../../../functions/utilities/array-utility';
import { TransitionElement } from '../../../model/enums/transition-element';
import { TransitionTargetSubsector } from '../../../model/enums/transition-target-subsector';
import { TracingService } from '../../tracing-service/tracing.service';
import { isMetricEnergyUnit } from '../../unit-conversion/is-metric-energy-unit';
import { EnergyUnit } from '../../unit-conversion/unit-conversion.constant';
import { ELECTRICITY } from '../ccc-algorithm/resource/resource';
import { CostType, ICarbonCausalChain, Output } from '../model/carbon-causal-chain';
import { EconomicStockImpactType } from '../model/climate-cortex-model';
import { IResource } from '../model/resource';
import {
  ITransitionElement,
  ShiftElement,
  SupplyAlterationElement,
  SupplyUpdateElement,
  TransitionElementProps,
  TransitionElementType,
  UpdateElement,
  UpdateFactorOptions,
  UpshiftUpdateProps,
} from '../model/transition-element';
import { IVariable, VariableType } from '../model/variable';
import { findVariablesInStringFunction } from '../utils/expression-util';
import { TransitionElementFilter } from './transition-element-filter/transition-element-filter';

export type Node = ClientNode | NodeListItem | ClientTarget;

@Injectable({ providedIn: 'root' })
export class CCCModelUtility {
  private _activityEnergyUnitDict: KeyMap<EnergyUnit> | undefined = undefined;
  private _cccs: { [key: string]: ICarbonCausalChain };
  private _energyVarsRelatedToElectricity: string[] | undefined = undefined;
  private _resources: { [key: string]: IResource };
  private _supplySideActivities: string[] | undefined = undefined;
  private _teEnergyUnitDict: KeyMap<EnergyUnit> | undefined = undefined;
  private _teEnergyUnitFromOutputDict: KeyMap<EnergyUnit> | undefined = undefined;
  private _transitionElements: { [key: string]: ITransitionElement };
  private _variables: { [key: string]: IVariable };

  private get _activityEnergyUnitDictionary(): KeyMap<EnergyUnit> {
    if (!this._activityEnergyUnitDict) {
      const electricityResources = [
        'electricity',
        'electricity_imported_grid',
        'wind_offshore',
        'wind_onshore',
        'solar_panel_parks',
        'solar_panel_rooftop',
        'hydro_reservoir',
      ];
      this._activityEnergyUnitDict = {};
      Object.values(this._cccs).forEach((ccc) => {
        if (ccc.work) {
          // assuming single work
          const work = ccc.work[0];
          const energyResources: string[] = [];
          if (work.unitOfMeasure === 'kwh') {
            work.input.forEach((input) => {
              energyResources.push(input.resource);
            });
          }
          if (work.output) {
            work.output.forEach((output) => {
              energyResources.push(output.resource);
            });
          }
          if (energyResources.length > 0) {
            const energyUnit = energyResources.every((resource) => electricityResources.includes(resource))
              ? EnergyUnit.KWH
              : EnergyUnit.MMBTU;
            this._activityEnergyUnitDict[ccc.ccc] = energyUnit;
          }
        }
      });
    }
    return this._activityEnergyUnitDict;
  }

  private get _energyVariablesRelatedToElectricity(): string[] {
    if (!this._energyVarsRelatedToElectricity) {
      const electricityResources = ['electricity', 'electricity_imported_grid'];
      const energyVariablesRelatedToElectricity: Set<string> = new Set<string>();
      Object.values(this._cccs).forEach((ccc) => {
        if (this.getSupplySideActivities().includes(ccc.ccc)) {
          if (ccc.work && ccc.work[0].output.every((output) => electricityResources.includes(output.resource))) {
            energyVariablesRelatedToElectricity.add(ccc.operation.variable);
            // supply side activities have no growth factor
          }
        } else {
          if (ccc.work && ccc.work[0].input.every((input) => electricityResources.includes(input.resource))) {
            // assuming single work item
            const energyVariables = this.getCccVariables(ccc.ccc, false).filter((v) =>
              isMetricEnergyUnit(this.getVariableDefinitionById(v.id)?.unitOfMeasure)
            );
            energyVariables.map((v) => v.id).forEach((id) => energyVariablesRelatedToElectricity.add(id));
          }
        }
      });
      this._energyVarsRelatedToElectricity = Array.from(energyVariablesRelatedToElectricity);
    }
    return this._energyVarsRelatedToElectricity;
  }

  private get _teEnergyUnitDictionary(): KeyMap<EnergyUnit> {
    if (!this._teEnergyUnitDict) {
      this._teEnergyUnitDict = {};
      Object.values(this._transitionElements).forEach((te) => {
        const cccEnergyUnits = this.getCccIds(te.transitionElement as TransitionElement).map((id) =>
          this.getUSEnergyUnitOfActivity(id)
        );
        if (cccEnergyUnits.length > 0) {
          this._teEnergyUnitDict[te.transitionElement as TransitionElement] = cccEnergyUnits.every(
            (unit) => unit === EnergyUnit.KWH
          )
            ? EnergyUnit.KWH
            : EnergyUnit.MMBTU;
        }
      });
    }
    return this._teEnergyUnitDict;
  }

  private get _teEnergyUnitFromOutputDictionary(): KeyMap<EnergyUnit> {
    if (!this._teEnergyUnitFromOutputDict) {
      this._teEnergyUnitFromOutputDict = {};
      Object.values(this._transitionElements).forEach((te) => {
        if ([TransitionElementType.SupplyAlteration, TransitionElementType.SupplyUpdate].includes(te.type)) {
          // check if the output is only electricity
          const cccId = this.getCccIds(te.transitionElement as TransitionElement)[0]; // there is only one CCC in supply TEs
          const outputs = this._cccs[cccId].work[0].output;
          this._teEnergyUnitFromOutputDict[te.transitionElement as TransitionElement] = outputs.every(
            (output) => output.resource === ELECTRICITY
          )
            ? EnergyUnit.KWH
            : EnergyUnit.MMBTU;
        }
      });
    }
    return this._teEnergyUnitFromOutputDict;
  }

  constructor(
    modelService: IClimateCortexModelService,
    private _tracingService: TracingService
  ) {
    this._transitionElements = modelService.transitionElements;
    this._cccs = modelService.carbonCausalChains;
    this._resources = modelService.resources;
    this._variables = modelService.variables;
  }

  canCalculateEconomicCase(transitionElement: TransitionElement): boolean {
    if (
      DEPLOYED_BUT_NOT_COMPATIBLE_WITH_ALGORITHM.includes(transitionElement) ||
      this.getTransitionType(transitionElement) !== TransitionElementType.Shift
    ) {
      return false;
    }
    const cccs = this.getCccIds(transitionElement).map((id) => this.getCccDefinitionById(id));
    return cccs.length > 1 && cccs.every((ccc) => ccc.costs?.length > 0); //First condition needed to filter out broken models
  }

  findInstantiatedTransitionElements(nodes: Node[]): {
    utilisation: CccAdapterTransitionTargetItem[];
    supply: CccAdapterTransitionTargetItem[];
  } {
    function _filterAndMapNodes(transitionElements: NodeListItem[], nodes: Node[]) {
      return transitionElements
        .filter(
          (target) =>
            nodeExistsInTree(target, nodes) &&
            targetHasActiveTransitionTargetData(target) &&
            !DEPLOYED_BUT_NOT_COMPATIBLE_WITH_ALGORITHM.includes(target.nodeProperties.transitionElement) // remove once all 'Deployed' TTs are supported
        )
        .map((n) => nodeListItemToCccAdapterTransitionTargetItem(n));
    }

    const utilisationNodes = [];
    const supplyNodes = [];
    nodes.forEach((node) => {
      if (node.nodeProperties.type === NodeType.Target) {
        utilisationNodes.push(node);
      } else if (node.nodeProperties.type === NodeType.SupplyTarget) {
        supplyNodes.push(node);
      }
    });
    const utilisationNodesWithInstantiatedModels = _filterAndMapNodes(utilisationNodes, nodes);
    const supplyNodesWithInstantiatedModels = _filterAndMapNodes(supplyNodes, nodes);
    return {
      utilisation: utilisationNodesWithInstantiatedModels,
      supply: supplyNodesWithInstantiatedModels,
    };
  }

  findResourcesToInstantiate(modelCccs: KeyMap<ICarbonCausalChain>): string[] {
    return this._tracingService.trace('findResourcesToInstantiate', () => {
      const modelDependentResourceNames = new Set<string>();
      // Process all CCCs with work, since we want to be able to calculate resources for those
      Object.entries(modelCccs)
        .filter((cccItem) => cccItem[1].work?.length > 0)
        .forEach((cccItem) => {
          const ccc: ICarbonCausalChain = cccItem[1];
          ccc.work.forEach((work) => {
            work.input.forEach((i) => {
              modelDependentResourceNames.add(i.resource);
            });
            work.output?.forEach((i) => {
              modelDependentResourceNames.add(i.resource);
            });
          });
        });

      return [...modelDependentResourceNames];
    });
  }

  getCccDefinitionById(cccId: string): ICarbonCausalChain | undefined {
    return this._cccs[cccId];
  }

  getCccIds(transitionElement: TransitionElement): string[] {
    const cccIds: Set<string> = new Set<string>();
    const transitionElementDefinition: ITransitionElement = this._transitionElements[transitionElement];
    if (transitionElementDefinition?.type === TransitionElementType.Shift) {
      (transitionElementDefinition as ShiftElement).props.shiftFrom.chains
        .concat((transitionElementDefinition as ShiftElement).props.shiftTo.chains)
        .forEach((chain) => {
          if (chain) {
            cccIds.add(chain);
          }
        });
    } else if (transitionElementDefinition) {
      (transitionElementDefinition.props as TransitionElementProps).carbonCausalChains.chains.forEach((chain) =>
        cccIds.add(chain)
      );
    }
    return Array.from(cccIds);
  }

  getCccVariables(cccId: string, strictExpressionMatch: boolean): { id: string; type: VariableType }[] {
    const ccc: ICarbonCausalChain = this._cccs[cccId];

    let variables = [
      { id: ccc.operation.variable, type: VariableType.OPERATIONS },
      ...this._getResourceVariables(ccc, strictExpressionMatch),
      ...(ccc.operation.growthFactor
        ? findVariablesInStringFunction(ccc.operation.growthFactor.expression, false, strictExpressionMatch).map(
            (v) => ({
              id: v,
              type: this.getVariableTypeFromId(v) ?? VariableType.GROWTH_FACTOR,
            })
          )
        : []),
    ];
    // filter out duplicates
    return [...new Map(variables.map((item) => [item.id, item])).values()];
  }

  getCostType(cccId: string, costName: string): CostType | undefined {
    return this._cccs[cccId].costs?.find((cost) => cost.name === costName)?.type;
  }

  getDefaultActivityUnit(transitionElement: TransitionElement): string {
    return this._transitionElements[transitionElement]?.unitOfMeasure || '';
  }

  getDeprecatedTransitionElements(): TransitionElement[] {
    return [
      TransitionElement.WindPower,
      TransitionElement.PvOnRoofTops,
      TransitionElement.ShiftToBiofuelInDistrictHeating,
      TransitionElement.ShiftToResidualHeatInDistrictHeating,
      TransitionElement.ShiftToHeatPumpsInDistrictHeating,
      TransitionElement.IncreasedEnergyRecoveryFromWaste,
      TransitionElement.ImprovedGridElectricity,
      TransitionElement.ShiftToBiogasHeatingOfNonResidentialCommercialBuildings,
      TransitionElement.ShiftToBiogasHeatingOfSingleFamilyResidentialBuildings,
      TransitionElement.RenewableRawMaterialsForRefineries,
      TransitionElement.BiofuelsForPaperAndPulpIndustry,
      TransitionElement.ElectrificationOfProductionInPaperAndPulpIndustry,
      TransitionElement.ElectrificationOfProductionInIronAndSteelIndustry,
      TransitionElement.InertAnodes,
      TransitionElement.RenewableHydrogen,
    ];
  }

  getDirectVariableDependencies(transitionElement: TransitionElement): Array<IVariable> {
    const getResourceVariables = (ccc: ICarbonCausalChain): string[] => {
      let resourceVariable: string[] = [];
      ccc.work?.forEach((work) => {
        resourceVariable = resourceVariable.concat(
          findVariablesInStringFunction(work.operationToWork.expression, false)
        );
        work.input.forEach((input) => {
          resourceVariable = resourceVariable.concat(
            findVariablesInStringFunction(input.resourceToWork.expression, false)
          );
          resourceVariable = resourceVariable.concat(
            findVariablesInStringFunction(input.emissionFactor.expression, false)
          );
          if (typeof input.resourceProportion === 'string') {
            resourceVariable.push(input.resourceProportion);
          }
        });
        work.output?.forEach((output) => {
          resourceVariable = resourceVariable.concat(
            findVariablesInStringFunction(output.workToResource.expression, false)
          );
        });
      });
      return resourceVariable;
    };
    const getCccVariables = (cccId: string): string[] => {
      const ccc: ICarbonCausalChain = this._cccs[cccId];
      let variables = [ccc.operation.variable, ...getResourceVariables(ccc)];
      if (ccc.operation.growthFactor) {
        variables = variables.concat(findVariablesInStringFunction(ccc.operation.growthFactor.expression, false));
      }
      return variables;
    };

    let variableNames: string[] = [];
    const transitionElementDefinition: ITransitionElement = this._transitionElements[transitionElement];
    if (transitionElementDefinition) {
      variableNames = variableNames.concat(
        this._findTransitionElementDependendentParameters(transitionElementDefinition, false)
      );
      this.getCccIds(transitionElement).forEach((cccId) => {
        variableNames = variableNames.concat(getCccVariables(cccId));
      });
      //special parameters, used in calculations (as of now) but not referenced by TT
      variableNames.push('population');
      variableNames.push('population_growth_rate');
    }
    const uniqueVariables = new Set(variableNames.map((p) => this._variables[p]));
    return Array.from(uniqueVariables);
  }

  // We are doing this in code until we know this is the correct path, then we can add it to model definitions instead
  getEconomicStockImpact(transitionElement: TransitionElement): EconomicStockImpactType {
    const tesWithNoImpact = [
      TransitionElement.IncreasedProportionOfWalkingAndCycling,
      TransitionElement.IncreasedProportionOfCommutingByElectricBus,
      TransitionElement.IncreasedProportionOfPublicTransportByDieselBus,
      TransitionElement.IncreasedProportionOfPublicTransportByGasBus,
    ];
    return tesWithNoImpact.includes(transitionElement) ? EconomicStockImpactType.None : EconomicStockImpactType.Full;
  }

  getFactorsToUpdate(transitionElement: TransitionElement): UpdateFactorOptions[] {
    const transitionElementDefinition: ITransitionElement = this._transitionElements[transitionElement];
    if (
      transitionElementDefinition?.type === TransitionElementType.Update ||
      transitionElementDefinition?.type === TransitionElementType.Upshift
    ) {
      return (transitionElementDefinition.props as UpshiftUpdateProps).factorsToUpdate;
    }
    return [];
  }

  getMaximumPotentialExpression(transitionElement: TransitionElement): string | undefined {
    const transitionElementDefinition: ITransitionElement = this._transitionElements[transitionElement];
    if (transitionElementDefinition?.type === TransitionElementType.SupplyAlteration) {
      return (transitionElementDefinition as SupplyAlterationElement).props.maximumPotential?.expression;
    }
    return undefined;
  }

  getOneSideCccIdsForShiftElement(transitionElement: TransitionElement, side: 'rhs' | 'lhs'): string[] {
    const transitionElementDefinition: ITransitionElement = this._transitionElements[transitionElement];
    if (transitionElementDefinition?.type === TransitionElementType.Shift) {
      return (transitionElementDefinition as ShiftElement).props[side === 'rhs' ? 'shiftTo' : 'shiftFrom'].chains;
    }
    return [];
  }

  getPrimaryProducedResource(teId: TransitionElement): string | undefined {
    const isSupply = this.isTransitionElementSupplyAlteration(teId) || this.isTransitionElementSupplyUpdate(teId);
    if (!isSupply) {
      return;
    }
    const cccIds = this.getCccIds(teId);
    const cccId = cccIds.length > 0 ? cccIds[0] : undefined; // Only one CCC in SupplyAlteration
    if (!cccId) {
      return;
    }
    const outputs: Output[] = this._cccs[cccId].work[0].output;
    const output = outputs?.length > 0 ? (outputs.find((o) => o.primary) ?? outputs[0]) : undefined;
    return output?.resource;
  }

  getPrimaryUpdatedParameterUnit(transitionElement: TransitionElement): string {
    const variable = this.getPrimaryUpdatedVariable(transitionElement);
    if (variable) {
      return this._variables[variable]?.unitOfMeasure;
    }
    return undefined;
  }

  getPrimaryUpdatedVariable(transitionElement: TransitionElement): string {
    const transitionElementDefinition: ITransitionElement = this._transitionElements[transitionElement];
    if (
      transitionElementDefinition?.type === TransitionElementType.Update ||
      transitionElementDefinition?.type === TransitionElementType.Upshift
    ) {
      const factors: string[] = (transitionElementDefinition.props as UpshiftUpdateProps).factorsToUpdate.map(
        (f) => f.update
      );
      if (factors.length > 0) {
        return factors.sort()[0];
      }
    } else if (transitionElementDefinition?.type === TransitionElementType.SupplyUpdate) {
      const variable: string = (transitionElementDefinition as SupplyUpdateElement).props.variableToUpdate;
      if (variable) {
        return variable;
      }
    }
    return undefined;
  }

  getResourceModel(resource: string): IResource {
    return this._resources[resource];
  }

  getResourceStandardUnitOfMeasure(resourceId: string): string {
    return this._resources[resourceId]?.standardUnitOfMeasure || '';
  }

  getResourceUnitOfMeasure(resourceId: string): string {
    const resource = this._resources[resourceId];
    if (resource.conversions?.length === 1) {
      // If we ever have more than one item, we need to know which one is the one used in calculations
      return resource.conversions[0].unitOfMeasure;
    } else {
      return resource.standardUnitOfMeasure;
    }
  }

  getSecondaryProducedResources(teId: TransitionElement): string[] {
    const isSupply = this.isTransitionElementSupplyAlteration(teId) || this.isTransitionElementSupplyUpdate(teId);
    if (!isSupply) {
      return [];
    }
    const cccIds = this.getCccIds(teId);
    const cccId = cccIds.length > 0 ? cccIds[0] : undefined; // Only one CCC in SupplyAlteration
    if (!cccId) {
      return [];
    }
    const outputs: Output[] = this._cccs[cccId].work[0].output;
    const secondaryOutputs: Output[] = outputs?.length > 1 ? outputs.filter((o) => !o.primary) : [];
    return secondaryOutputs.map((o) => o.resource);
  }

  getSector(transitionElement: TransitionElement): TransitionTargetSector {
    if (isTransport(transitionElement)) {
      return TransitionTargetSector.TRANSPORT;
    } else if (isIndustry(transitionElement)) {
      return TransitionTargetSector.INDUSTRY;
    } else if (isAgriculture(transitionElement)) {
      return TransitionTargetSector.AGRICULTURE;
    } else if (isEnergy(transitionElement)) {
      return TransitionTargetSector.ENERGY;
    } else if (isOther(transitionElement)) {
      return TransitionTargetSector.OTHER;
    }
    return undefined;
  }

  // until we have a type property on the ccc/activity model, we consider an activity to be on the supply side if it is referred to by a supply alteration transition element
  getSupplySideActivities(): string[] {
    if (!this._supplySideActivities) {
      const supplySideActivities: Set<string> = new Set<string>();
      Object.values(this._transitionElements)
        .filter((t) => t.type === TransitionElementType.SupplyAlteration)
        .forEach((te) => {
          (te as SupplyAlterationElement).props.carbonCausalChains.chains.forEach((chain) => {
            supplySideActivities.add(chain);
          });
        });
      this._supplySideActivities = Array.from(supplySideActivities);
    }
    return this._supplySideActivities;
  }

  getTransitionElementsBySubsector(subsector: TransitionTargetSubsector): TransitionElement[] {
    return Object.values(this._transitionElements)
      .filter((te) => getSubsectorForTransitionElement(te.transitionElement as TransitionElement) === subsector)
      .map((te) => te.transitionElement as TransitionElement);
  }

  getTransitionElementsFromCcc(cccId: string, filters: TransitionElementFilter[] = []): TransitionElement[] {
    const transitionElementsFromModel: TransitionElement[] = Object.keys(this._transitionElements).filter((key) => {
      const transitionElementDefinition: ITransitionElement = this._transitionElements[key];
      if (transitionElementDefinition?.type === TransitionElementType.Shift) {
        return (transitionElementDefinition as ShiftElement).props.shiftFrom.chains
          .concat((transitionElementDefinition as ShiftElement).props.shiftTo.chains)
          .includes(cccId);
      } else if (transitionElementDefinition) {
        return (transitionElementDefinition.props as TransitionElementProps).carbonCausalChains.chains.includes(cccId);
      } else {
        return false;
      }
    }) as TransitionElement[];
    const filteredTransitionElements: TransitionElement[] = filters.reduce((transitionElements, filter) => {
      return filter.filter(transitionElements);
    }, transitionElementsFromModel);
    return filteredTransitionElements;
  }

  getTransitionElementsTargetedCccIds(tEId: TransitionElement): string[] {
    return this.isTransitionElementShift(tEId)
      ? this.getOneSideCccIdsForShiftElement(tEId, 'lhs')
      : this.getCccIds(tEId);
  }

  getTransitionType(transitionElement: TransitionElement): TransitionElementType {
    return (this._transitionElements[transitionElement]?.type as TransitionElementType) || undefined;
  }

  getUnitByVariableId(variableId: string): string {
    return this._variables[variableId]?.unitOfMeasure || '';
  }

  getUSEnergyUnitOfActivity(cccId: string): EnergyUnit | undefined {
    return this._activityEnergyUnitDictionary[cccId];
  }

  getUSEnergyUnitOfTransitionElement(teId: string): EnergyUnit | undefined {
    return this._teEnergyUnitDictionary[teId];
  }

  getUSEnergyUnitOfTransitionElementFromOutput(teId: string): EnergyUnit | undefined {
    return this._teEnergyUnitFromOutputDictionary[teId];
  }

  getVariableDefinitionById(id: string): IVariable | undefined {
    return this._variables[id];
  }

  getVariableTypeFromId(variableId: string): VariableType | undefined {
    return this.getVariableDefinitionById(variableId)?.type;
  }

  hasCosts(cccId: string): boolean {
    return this.getCccDefinitionById(cccId)?.costs?.length > 0;
  }

  hasDefinedModel(transitionElement: TransitionElement): boolean {
    return (
      this.modelExists(transitionElement) && !DEPLOYED_BUT_NOT_COMPATIBLE_WITH_ALGORITHM.includes(transitionElement)
    );
  }

  isEnergyVariableRelatedToElectricity(variableId: string): boolean {
    return this._energyVariablesRelatedToElectricity.includes(variableId);
  }

  isTransitionElementCHPSupplyAlteration(teId: TransitionElement): boolean {
    if (this.isTransitionElementSupplyAlteration(teId)) {
      const cccId = this.getCccIds(teId)[0];
      const cccWork = this._cccs[cccId].work[0];
      if (cccWork.output?.length > 1) {
        return true;
      }
    }
    return false;
  }

  isTransitionElementUpdate(ttId: TransitionElement): boolean {
    return this._isOfType(ttId, TransitionElementType.Update);
  }

  isTransitionElementUpshift(ttId: TransitionElement): boolean {
    return this._isOfType(ttId, TransitionElementType.Upshift);
  }

  isTransitionElementResourceShift(ttId: TransitionElement): boolean {
    return this._isOfType(ttId, TransitionElementType.ResourceShift);
  }

  isTransitionElementShift(ttId: TransitionElement): boolean {
    return this._isOfType(ttId, TransitionElementType.Shift);
  }

  isTransitionElementSupplyAlteration(ttId: TransitionElement): boolean {
    return this._isOfType(ttId, TransitionElementType.SupplyAlteration);
  }

  isTransitionElementSupplyUpdate(ttId: TransitionElement): boolean {
    return this._isOfType(ttId, TransitionElementType.SupplyUpdate);
  }

  isUtilisationTransitionElement(teId: TransitionElement): boolean {
    return (
      this.isTransitionElementUpdate(teId) ||
      this.isTransitionElementUpshift(teId) ||
      this.isTransitionElementResourceShift(teId) ||
      this.isTransitionElementShift(teId)
    );
  }

  modelExists(transitionElement: TransitionElement): boolean | null {
    return Object.keys(this._transitionElements).includes(transitionElement);
  }

  isParameterRelatedToElectricity(parameterName: string): boolean {
    if (this._isNormalParameterRelatedToElectricity(parameterName)) {
      return true;
    }

    if (this._isEconomicParameterRelatedToElectricity(parameterName)) {
      return true;
    }

    return false;
  }

  private _findTransitionElementDependendentParameters(
    transitionElement: ITransitionElement,
    strict: boolean = true
  ): string[] {
    let dependencies: string[] = [];
    dependencies = dependencies.concat(findVariablesInStringFunction(transitionElement.cohort.expression, strict));
    if (transitionElement.type === TransitionElementType.Shift) {
      dependencies = dependencies.concat(
        findVariablesInStringFunction((transitionElement as ShiftElement).props.shiftFrom.atoc, strict)
      );
      dependencies = dependencies.concat(
        findVariablesInStringFunction((transitionElement as ShiftElement).props.shiftTo.atoc, strict)
      );
    } else {
      dependencies = dependencies.concat(
        findVariablesInStringFunction(
          (transitionElement.props as TransitionElementProps).carbonCausalChains.atoc,
          strict
        )
      );
      if (
        transitionElement.type === TransitionElementType.Update ||
        transitionElement.type === TransitionElementType.Upshift
      ) {
        dependencies = dependencies.concat(
          findVariablesInStringFunction((transitionElement as UpdateElement).props.weightInversionExpression, strict)
        );
        (transitionElement as UpdateElement).props.factorsToUpdate.forEach((factor) => {
          dependencies.push(factor.update);
          dependencies = dependencies.concat(findVariablesInStringFunction(factor.by.expression, strict));
        });
      } else if (transitionElement.type === TransitionElementType.SupplyUpdate) {
        dependencies = dependencies.concat(
          findVariablesInStringFunction(
            (transitionElement as SupplyUpdateElement).props.weightInversionExpression,
            strict
          )
        );
        dependencies.push((transitionElement as SupplyUpdateElement).props.variableToUpdate);
      } else if (transitionElement.type === TransitionElementType.SupplyAlteration) {
        dependencies = dependencies.concat(
          findVariablesInStringFunction(
            this.getMaximumPotentialExpression(transitionElement.transitionElement as TransitionElement),
            strict
          )
        );
      }
    }
    return dependencies.filter(unique);
  }

  private _getResourceVariables(
    ccc: ICarbonCausalChain,
    strictExpressionMatch: boolean
  ): { id: string; type: VariableType }[] {
    return (
      ccc.work?.flatMap((work) => [
        ...findVariablesInStringFunction(work.operationToWork.expression, false, strictExpressionMatch).map((id) => ({
          id,
          type: this.getVariableTypeFromId(id) ?? VariableType.ENERGY_INTENSITY,
        })),

        ...work.input.flatMap((input) => [
          ...findVariablesInStringFunction(input.resourceToWork.expression, false, strictExpressionMatch).map((id) => ({
            id,
            type: this.getVariableTypeFromId(id) ?? VariableType.RESOURCE_COEFFICIENT,
          })),

          ...findVariablesInStringFunction(input.emissionFactor.expression, false, strictExpressionMatch).map((id) => ({
            id,
            type: this.getVariableTypeFromId(id) ?? VariableType.EMISSION_FACTOR,
          })),

          ...(typeof input.resourceProportion === 'string'
            ? [
                {
                  id: input.resourceProportion,
                  type: this.getVariableTypeFromId(input.resourceProportion) ?? VariableType.RESOURCE_PROPORTION,
                },
              ]
            : []),
        ]),
      ]) || []
    );
  }

  private _getTransitionElementsFromVariable(variableName: string): TransitionElement[] {
    return Array.from(
      new Set(
        Object.values(this._transitionElements)
          .filter((te) => this._findTransitionElementDependendentParameters(te).includes(variableName))
          .map((element) => element.transitionElement as TransitionElement)
      )
    );
  }

  private _isOfType(ttId: TransitionElement, type: TransitionElementType): boolean {
    return this._transitionElements[ttId]?.type === type;
  }

  private _isEconomicParameterRelatedToElectricity(parameterName: string): boolean {
    const relatedCccs = Object.values(this._cccs).filter((ccc) =>
      ccc.costs?.some((cost) => findVariablesInStringFunction(cost.costFactor.expression).includes(parameterName))
    );

    if (relatedCccs.length === 0) {
      return false;
    }

    return relatedCccs.every((ccc) => this.getUSEnergyUnitOfActivity(ccc.ccc) === EnergyUnit.KWH);
  }

  private _isNormalParameterRelatedToElectricity(parameterName: string): boolean {
    const transitionElements = this._getTransitionElementsFromVariable(parameterName);

    if (transitionElements.length > 0) {
      const areAllKwh = transitionElements.every(
        (te) => this.getUSEnergyUnitOfTransitionElement(te) === EnergyUnit.KWH
      );
      if (areAllKwh) {
        return true;
      }
    }
    return null;
  }
}
